import { create } from 'zustand'
import { analyticsApi } from '../services/api'
import type { DashboardData } from '../types'

interface AnalyticsState {
  dashboard: DashboardData | null
  videoAnalytics: Record<string, unknown> | null
  linkAnalytics: Record<string, unknown> | null
  isLoading: boolean
  error: string | null
  periodDays: number

  // Actions
  fetchDashboard: (days?: number) => Promise<void>
  fetchVideoAnalytics: (videoId: string, days?: number) => Promise<void>
  fetchLinkAnalytics: (linkId: string, days?: number) => Promise<void>
  setPeriodDays: (days: number) => void
  clearError: () => void
}

export const useAnalyticsStore = create<AnalyticsState>((set, get) => ({
  dashboard: null,
  videoAnalytics: null,
  linkAnalytics: null,
  isLoading: false,
  error: null,
  periodDays: 30,

  fetchDashboard: async (days?: number) => {
    const periodDays = days ?? get().periodDays
    set({ isLoading: true, error: null })
    try {
      const dashboard = await analyticsApi.getDashboard(periodDays)
      set({ dashboard, isLoading: false, periodDays })
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      set({
        error: error.response?.data?.error || 'Failed to fetch dashboard',
        isLoading: false,
      })
    }
  },

  fetchVideoAnalytics: async (videoId: string, days?: number) => {
    const periodDays = days ?? get().periodDays
    set({ isLoading: true, error: null })
    try {
      const videoAnalytics = await analyticsApi.getVideoAnalytics(videoId, periodDays)
      set({ videoAnalytics, isLoading: false })
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      set({
        error: error.response?.data?.error || 'Failed to fetch video analytics',
        isLoading: false,
      })
    }
  },

  fetchLinkAnalytics: async (linkId: string, days?: number) => {
    const periodDays = days ?? get().periodDays
    set({ isLoading: true, error: null })
    try {
      const linkAnalytics = await analyticsApi.getLinkAnalytics(linkId, periodDays)
      set({ linkAnalytics, isLoading: false })
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      set({
        error: error.response?.data?.error || 'Failed to fetch link analytics',
        isLoading: false,
      })
    }
  },

  setPeriodDays: (days: number) => set({ periodDays: days }),

  clearError: () => set({ error: null }),
}))
